import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

import { isMobile } from 'react-device-detect';

import { Row, Modal, notification } from 'antd';

import FormHeader from '@controls/form-header/form-header';

import InboundCarrierWeb from './inbound-carrier-web';

import { exception } from '@extensions/notification';
import { getEnumList } from '@extensions/utils';

import { userLoaded } from '@store/actions';
import { useAppDispatch, useAppSelector } from '@store/hooks';

import { serverFetch } from '@src/server';

import { IUserSession } from '@src/entities/user-session';
import { IInboundCarrier } from '@src/entities/inbound-carrier';
import { ICountry } from '@entities/country';

import { InboundCarrierStatus } from '@src/enums/inbound-carrier-status';
import { CargoCarrierType, enumLabel as cargoCarrierTypeLabel } from '@enums/cargo-carrier-type';
import { Permission, hasPermission } from '@src/enums/permission';
import { Currency } from '@enums/currency';
import { IEnumItem } from '@enums/enum-item';

dayjs.extend(utc);

const InboundCarrier = () => {
    const navigate = useNavigate();
    const [api, notificationContextHolder] = notification.useNotification();

    const d = useAppDispatch();
    const userSession = useAppSelector<IUserSession>((s) => s.userSession);

    const { id } = useParams();

    const [modal, modalContextHolder] = Modal.useModal();

    const [entity, setEntity] = useState<IInboundCarrier>({
        id: undefined,
        number: undefined,
        cargoCarrierType: undefined,
        etdOn: undefined,
        status: InboundCarrierStatus.New,
        charteringCurrency: Currency.Eur,
    });
    const [loading, setLoading] = useState<boolean>(false);
    const [canManageCarrier] = useState<boolean>(hasPermission(userSession.permissions, Permission.ManageInboundCarrier));
    const [countries, setCountries] = useState<Array<ICountry>>([]);
    const [cargoCarrierTypes] = useState<Array<IEnumItem>>(getEnumList(CargoCarrierType, cargoCarrierTypeLabel));

    useEffect(() => {
        let cleanup = false;

        const fetchData = async () => {
            setLoading(true);

            const promises = [
                await serverFetch(`countries`, { method: 'GET' })
                    .then((data) => {
                        return data;
                    })
                    .catch((ex) => {
                        exception(api, 'Ошибка получения стран', ex, () => d(userLoaded(undefined)));
                    }),
            ];

            if (id) {
                promises.push(
                    await serverFetch(`inboundcarriers/${id}`, { method: 'GET' })
                        .then((data) => {
                            return data;
                        })
                        .catch((ex) => {
                            exception(api, 'Ошибка получения рейса', ex, () => d(userLoaded(undefined)));
                        }),
                );
            }

            Promise.all([promises]).then((result) => {
                if (cleanup) return;

                setCountries(result[0][0]);

                if (id) {
                    setEntity(result[0][1]);
                }

                setLoading(false);
            });
        };

        fetchData();

        return () => {
            cleanup = true;
        };
    }, []);

    const onFinish = () => {
        setLoading(true);

        const result = { ...entity, documents: [], internalDocuments: [] };

        serverFetch(`inboundcarriers`, { method: id ? 'PUT' : 'POST', bodyData: result })
            .then(() => {
                setLoading(false);
                navigate(-1);
            })
            .catch((ex) => {
                setLoading(false);
                exception(api, 'Ошибка сохранения рейса', ex, () => d(userLoaded(undefined)));
            });
    };

    const onRevertOnway = () => {
        serverFetch(`warehouse/acceptance/${id}/revert`, { method: 'POST' })
            .then(() => {
                navigate(-1);
            })
            .catch((ex) => {
                setLoading(false);
                exception(api, 'Ошибка изменения статуса', ex, () => d(userLoaded(undefined)));
            });
    };

    return (
        <>
            <Row>
                <FormHeader title={`${id ? (canManageCarrier ? 'Информация о рейсе' : 'Управление рейсом') : 'Добавить рейс'}`} />
            </Row>

            {!loading && (
                <InboundCarrierWeb
                    carrier={entity}
                    countries={countries}
                    canManageCarrier={canManageCarrier}
                    cargoCarrierTypes={cargoCarrierTypes}
                    modal={modal}
                    loading={loading}
                    api={api}
                    onFinish={onFinish}
                    onChange={(e: IInboundCarrier) => setEntity(e)}
                    onRevertOnway={onRevertOnway}
                    onClose={() => navigate(-1)}
                />
            )}

            {notificationContextHolder}
            {modalContextHolder}
        </>
    );
};

export default InboundCarrier;
